// ============================================================
// KVKK — rıza yönetimi, saklama süresi ve veri sahibi hakları (§9)
//
// Sistem çocukların SAĞLIK VERİSİNİ (alerji, ilaç, kan grubu, tanı) ve
// GÖRSELİNİ işliyor. Bunlar özel nitelikli kişisel veri kategorisindedir.
// KKTC'de 3/2020 sayılı Kişisel Verilerin Korunması Yasası yürürlükte;
// AB'ye hizmet veriliyorsa GDPR de devreye girer.
//
// Bu modül üç şeyi karşılar:
//   1. Rıza — ne için, kimden, ne zaman alındı (her amaç ayrı)
//   2. Saklama — veri ne kadar tutulacak, süre dolunca ne olacak
//   3. Haklar — velinin veriyi görme, dışa aktarma, silme talebi
// ============================================================

import { BUGUN } from "../config.js";
import { B, subeBaglaminda, subeBulHerYerde, subeninKiracisi } from "./context.js";
import { alerjiler, saglik, veliler } from "./students.js";
import { ogrencininIlaclari, ogrencininOlaylari } from "./health.js";
import { faturalarOf } from "./invoices.js";
import { denetimIzi } from "../repo/index.js";

/**
 * Rıza türleri. Her amaç AYRI rıza gerektirir — velilerin çoğu kurum
 * içi fotoğraf kullanımına izin verip sosyal medyaya vermez; tek bir
 * "fotoğraf onayı" kutusu bu ayrımı yok eder ve hukuken yetersiz kalır.
 */
export const RIZA_TURU = {
  photoInternal: {
    label: "Fotoğraf — kurum içi",
    aciklama: "Günlük rapor, portfolyo ve veli paylaşımında kullanılır.",
    zorunlu: false,
  },
  photoSocial: {
    label: "Fotoğraf — sosyal medya",
    aciklama: "Kurumun sosyal medya hesaplarında paylaşılabilir.",
    zorunlu: false,
  },
  healthData: {
    label: "Sağlık verisi işleme",
    aciklama: "Alerji, ilaç, tanı ve acil müdahale bilgisi. Özel nitelikli veridir.",
    zorunlu: true,
  },
  emergencyContact: {
    label: "Acil durum iletişimi",
    aciklama: "Acil durumda kayıtlı kişilerin aranması.",
    zorunlu: true,
  },
  tripPermission: {
    label: "Gezi ve etkinlik katılımı",
    aciklama: "Kurum dışı etkinliklere katılım izni.",
    zorunlu: false,
  },
};

/** Saklama süreleri (yıl). Mali kayıtlar yasal zorunlulukla daha uzun. */
export const SAKLAMA_SURESI = {
  ogrenciDosyasi: 2,
  saglikKaydi: 5,
  olayTutanagi: 10,
  maliKayit: 10,
  fotograf: 1,
  denetimIzi: 5,
};

export const AYDINLATMA_METNI = `
Yeşilada Eğitim Kurumları olarak, çocuğunuzun ve sizin kişisel verilerinizi
3/2020 sayılı Kişisel Verilerin Korunması Yasası kapsamında veri sorumlusu
sıfatıyla işliyoruz.

İşlenen veriler: kimlik ve iletişim bilgileri, çocuğun sağlık verisi (alerji,
ilaç, tanı), görsel kayıt, devam ve ödeme kayıtları.

İşleme amacı: eğitim hizmetinin sunulması, çocuğun güvenliği ve sağlığının
korunması, yasal yükümlülüklerin yerine getirilmesi.

Saklama: öğrenci dosyası ayrılıştan sonra 2 yıl, sağlık kaydı 5 yıl, olay
tutanağı 10 yıl, mali kayıtlar yasal süre boyunca saklanır.

Haklarınız: verilerinize erişme, düzeltilmesini isteme, dışa aktarma ve
silinmesini talep etme hakkına sahipsiniz. Talepleriniz için kurum yönetimine
başvurabilirsiniz.
`.trim();

// ---------- Rıza ----------

/** Öğrencinin rıza kayıtları. Kayıt yoksa rıza YOK sayılır. */
export function rizalar(student) {
  const kayit = student.consents ?? {};
  return Object.entries(RIZA_TURU).map(([kod, tanim]) => ({
    kod,
    ...tanim,
    granted: kayit[kod]?.granted ?? false,
    at: kayit[kod]?.at ?? null,
    by: kayit[kod]?.by ?? null,
  }));
}

export const rizaVar = (student, kod) => student.consents?.[kod]?.granted === true;

/** Zorunlu rızası eksik öğrenciler — bu veriler işlenmemeli. */
export function eksikRizalar(branchId) {
  const t = branchId ? subeBulHerYerde(branchId) : B();
  return t.students
    .map(s => ({
      student: s,
      eksik: rizalar(s).filter(r => r.zorunlu && !r.granted),
    }))
    .filter(x => x.eksik.length);
}

/**
 * Fotoğrafın belirli bir amaçla kullanılıp kullanılamayacağı.
 * Kurum içi rıza sosyal medya için YETMEZ.
 */
export const fotografKullanilabilir = (student, amac = "photoInternal") =>
  Boolean(student.photo) && rizaVar(student, amac);

// ---------- Saklama ----------

const yilFark = (a, b) =>
  (new Date(a + "T00:00:00Z") - new Date(b + "T00:00:00Z")) / (365.25 * 86400000);

/** Bir tarihe N takvim yılı ekler. */
function yilEkle(iso, yil) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCFullYear(d.getUTCFullYear() + yil);
  return d.toISOString().slice(0, 10);
}

/**
 * Ayrılmış öğrencinin verisinin saklama durumu.
 * Aktif öğrencide saklama süresi işlemeye başlamaz.
 *
 * Süre dolumu takvim yılıyla hesaplanır: "1 yıl" kuralı ayrılış
 * tarihinin yıldönümünde dolar. Gün sayısıyla (365.25) karşılaştırmak
 * tam bir takvim yılını eşiğin altında bırakıyordu.
 */
export function saklamaDurumu(student, bugun = BUGUN) {
  if (student.status !== "ayrildi" || !student.leftAt) {
    return { aktif: true, kategoriler: [] };
  }

  const gecen = yilFark(bugun, student.leftAt);
  const kategoriler = Object.entries(SAKLAMA_SURESI).map(([kod, yil]) => ({
    kod,
    yil,
    kalanYil: Math.max(0, Math.round((yil - gecen) * 10) / 10),
    silinmeli: bugun >= yilEkle(student.leftAt, yil),
  }));

  return {
    aktif: false,
    leftAt: student.leftAt,
    gecenYil: Math.round(gecen * 10) / 10,
    kategoriler,
    silinecekVar: kategoriler.some(k => k.silinmeli),
  };
}

/** Saklama süresi dolmuş veri taşıyan öğrenciler. */
export function saklamaSuresiDolanlar(branchId) {
  const t = branchId ? subeBulHerYerde(branchId) : B();
  return t.students
    .map(s => ({ student: s, durum: saklamaDurumu(s) }))
    .filter(x => !x.durum.aktif && x.durum.silinecekVar);
}

// ---------- Veri sahibi hakları ----------

/**
 * Velinin veri taşınabilirliği hakkı: çocuğa ait tüm veriyi
 * makine okunur biçimde döner. Silme talebinde de bu döküm esas alınır.
 */
export function veriDokumu(student, branchId) {
  const tid = branchId ?? B().id;
  const t = subeBulHerYerde(tid);
  return subeBaglaminda(tid, () => ({
    olusturulma: new Date().toISOString(),
    kurum: subeninKiracisi(tid)?.name ?? "—",
    sube: t.name,

    kimlik: {
      ad: student.name,
      dogumTarihi: student.birthDate,
      cinsiyet: student.gender,
      kayitTarihi: student.enrolledAt,
      sinif: t.classes.find(c => c.id === student.classId)?.name ?? null,
      program: student.schedule,
      servis: student.service,
    },

    veliler: veliler(student, tid),

    saglik: {
      alerjiler: alerjiler(student, tid),
      dosya: saglik(student, tid),
      ilaclar: ogrencininIlaclari(student.id, tid).map(m => ({
        ilac: m.drugName,
        doz: m.dose,
        endikasyon: m.indication,
        izin: m.consent,
        uygulamalar: m.administrations,
      })),
      olaylar: ogrencininOlaylari(student.id, tid),
    },

    devam: Object.entries(t.attendanceLog ?? {})
      .map(([gun, kayit]) => ({ gun, ...kayit[student.id] }))
      .filter(x => x.status)
      .sort((a, b) => b.gun.localeCompare(a.gun)),

    mali: faturalarOf(student.id).map(inv => ({
      donem: inv.period,
      vade: inv.due,
      hareketler: inv.transactions ?? [],
    })),

    rizalar: rizalar(student),

    islemGecmisi: denetimIzi({ branchId: tid, entityId: student.id, limit: 500 })
      .map(m => ({ tarih: m.at, islem: m.label, yapan: m.actor?.name })),
  }));
}

/** Dökümü indirilebilir JSON'a çevirir. */
export function dokumDosyasi(student, branchId) {
  const veri = veriDokumu(student, branchId);
  const ad = student.name.toLocaleLowerCase("tr-TR").replace(/\s+/g, "-");
  return {
    dosyaAdi: `${ad}-veri-dokumu-${BUGUN}.json`,
    icerik: JSON.stringify(veri, null, 2),
    tur: "application/json",
  };
}

// ---------- Özet ----------

export function kvkkOzeti(branchId) {
  const t = branchId ? subeBulHerYerde(branchId) : B();
  const eksik = eksikRizalar(t.id);
  const saklama = saklamaSuresiDolanlar(t.id);

  const rizaSayaci = {};
  for (const kod of Object.keys(RIZA_TURU)) {
    rizaSayaci[kod] = t.students.filter(s => rizaVar(s, kod)).length;
  }

  return {
    toplamOgrenci: t.students.length,
    eksikZorunluRiza: eksik.length,
    saklamaSuresiDolan: saklama.length,
    rizaSayaci,
    fotografli: t.students.filter(s => s.photo).length,
    sosyalMedyaIzinli: t.students.filter(s => rizaVar(s, "photoSocial")).length,
  };
}
